import _ from 'lodash';
import moment from 'moment';
import { createContext, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { Box, Flex } from 'reflexbox';

import { DeleteIcon } from '@treams/treams-icons';
import { Button, Empty, Heading, IconButton, Paragraph, Popover, Switch } from '@treams/treams-ui';

import {
	CycleInstance,
	CycleStatus,
	CycleTemplate,
	MomentInstance,
	User,
	useCycleQuery,
	useRemoveCycleInstanceMutation,
	useUserCyclesQuery,
} from 'generated/graphql';
import { CycleMomentsPreview } from 'packages/kakapo-ui-kit/components/cycles/CycleMomentsPreview';
import useLocalize from 'packages/kakapo-ui-application/hooks/useLocalize';

import AssignCycle from './AssignCycle';
import CycleMomentSkipModal, {
	CycleMomentInstance,
} from 'packages/kakapo-ui-kit/components/cycles/CycleMomentSkipModal';

export const CycleMomentsContext = createContext<{
	selectedMoments: CycleMomentInstance[];
	setSelectedMoment: (moment: CycleMomentInstance) => void;
}>({
	selectedMoments: [],
	setSelectedMoment: () => new Error('Function has not been defined'),
});

type CycleProps = {
	id: string;
	template: CycleTemplate;
};

function Cycle({ id }: CycleProps) {
	const { data } = useCycleQuery({ variables: { id } });
	const monthsRange = [-2, 4];

	const now = moment.utc().startOf('day');

	const bounds: [Date, Date] = [
		now.clone().add(monthsRange[0], 'months').toDate(),
		now.clone().add(monthsRange[1], 'months').toDate(),
	];

	if (!data) {
		return <CycleSkeleton />;
	}

	return (
		<CycleMomentsPreview
			dateRange={bounds}
			allowPagination
			cyclesLoading={false}
			momentsLoading={false}
			cycles={[data.cycleInstance as CycleInstance]}
			moments={data.cycleInstance.moments as MomentInstance[]}
		/>
	);
}

export default function Cycles(props: { id: string }) {
	const { t } = useTranslation('', { keyPrefix: 'settings.employees.cycles' });
	const { localize } = useLocalize();

	const [all, setAll] = useState(false);
	const [showSkipModal, setShowSkipModal] = useState(false);

	const query = useUserCyclesQuery({
		variables: {
			id: props.id,
			status: all ? [] : [CycleStatus.Active],
		},
		fetchPolicy: 'network-only',
	});
	const [remove] = useRemoveCycleInstanceMutation();

	const [adding, setAdding] = useState(false);

	const assign = query.data && (
		<AssignCycle
			user={query.data.user as User}
			onCancel={() => setAdding(false)}
			onDismiss={() => setAdding(false)}
			onAssign={async () => {
				await query.refetch();
			}}
		/>
	);

	const [selectedMoments, setSelectedMoments] = useState<CycleMomentInstance[]>([]);

	const handleSetSelectedMoment = (moment: CycleMomentInstance) => {
		if (!selectedMoments.some(({ instance }) => instance?.id === moment.instance?.id)) {
			setSelectedMoments([...selectedMoments, moment]);
		} else {
			setSelectedMoments(
				selectedMoments.filter(({ instance }) => instance?.id !== moment.instance?.id),
			);
		}
	};

	return (
		<Box mb={5}>
			<Flex mb={2} alignItems="center">
				<Heading flexGrow={1} flexShrink={1}>
					{t('title')}
				</Heading>
				<Switch on={all} onChange={() => setAll((all) => !all)} label={t('archive')} />
			</Flex>
			{query.data ? (
				<Paragraph mb={3}>{t('description', { name: query.data.user.firstName })}</Paragraph>
			) : (
				<Paragraph.Skeleton lines={1} mb={3} />
			)}
			{query.data ? (
				query.data.user.cycles.edges.length === 0 ? (
					<Empty.Card
						title={t('empty.title')}
						description={t('empty.description', {
							name: query.data.user.firstName,
						})}
						footer={
							<Box mt={3}>
								<Popover.Target visible={adding} contents={assign}>
									<Button
										type="transparent"
										title={t('assignButton')}
										onClick={() => setAdding(true)}
									/>
								</Popover.Target>
							</Box>
						}
					/>
				) : (
					<>
						{_.sortBy(
							query.data.user.cycles.edges,
							({ node }) => localize(node.cycleTemplate.title).text,
						).map(({ node }) => (
							<Box key={node.id} mb={4}>
								<Heading mb={2}>
									{localize(node.cycleTemplate.title).text} – (
									{t('from', {
										date: moment(node.startDate).format('DD MMM YYYY'),
									})}
									){' – '}
									{t(`status.${node.status.toLowerCase()}`)}
								</Heading>
								<Flex alignItems="center">
									<Box flexGrow={1} flexShrink={1}>
										<CycleMomentsContext.Provider
											value={{
												selectedMoments,
												setSelectedMoment: handleSetSelectedMoment,
											}}
										>
											<Cycle id={node.id} template={node.cycleTemplate as CycleTemplate} />
										</CycleMomentsContext.Provider>
									</Box>
									<Box ml={3} flexGrow={0} flexShrink={0}>
										<IconButton
											type="destructive"
											icon={<DeleteIcon />}
											onClick={async () => {
												await remove({ variables: { input: { id: node.id } } });
												await query.refetch();
											}}
										/>
									</Box>
								</Flex>
							</Box>
						))}

						<Flex>
							<Popover.Target visible={adding} contents={assign}>
								<Button
									type="transparent"
									title={t('assignButton')}
									onClick={() => setAdding(true)}
								/>
							</Popover.Target>

							{showSkipModal && (
								<CycleMomentSkipModal
									moments={selectedMoments}
									onDismiss={() => setShowSkipModal(false)}
								/>
							)}
							<Button
								ml={2}
								type="transparent"
								title={
									<Trans
										t={t}
										i18nKey={'bulkMomentSkipButton.title'}
										values={{ count: selectedMoments.length }}
										components={[]}
									/>
								}
								disabled={selectedMoments.length < 1}
								onClick={() => setShowSkipModal(true)}
							/>
						</Flex>
					</>
				)
			) : (
				<CycleSkeleton />
			)}
		</Box>
	);
}

function CycleSkeleton() {
	return <CycleMomentsPreview />;
}
